/**
 * Social platforms a creator can list in "Mis redes", and how a bare @handle
 * becomes a profile URL. Used by MyLinksSection and the demo customizer.
 */
export interface SocialPlatform {
  id: string
  name: string
  /** Two-letter badge in the list */
  tag: string
  /** Default line under the name (overridden by t.mylinks_<id>_desc) */
  desc: string
  /** Profile URL for a handle without @ */
  url: (handle: string) => string
  /** Highlighted (paid platforms) */
  accent?: boolean
}

export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  { id: 'instagram', name: 'Instagram', tag: 'IG', desc: 'Fotos · stories', url: (h) => `https://instagram.com/${h}` },
  { id: 'tiktok', name: 'TikTok', tag: 'TT', desc: 'Cortos · trends', url: (h) => `https://www.tiktok.com/@${h}` },
  { id: 'x', name: 'X', tag: 'X', desc: 'Diario · previews', url: (h) => `https://x.com/${h}` },
  { id: 'onlyfans', name: 'OnlyFans', tag: 'OF', desc: 'VIP · exclusivo', url: (h) => `https://onlyfans.com/${h}`, accent: true },
  { id: 'fansly', name: 'Fansly', tag: 'FY', desc: 'VIP · exclusivo', url: (h) => `https://fansly.com/${h}`, accent: true },
  { id: 'youtube', name: 'YouTube', tag: 'YT', desc: 'Videos · vlogs', url: (h) => `https://youtube.com/@${h}` },
  { id: 'twitch', name: 'Twitch', tag: 'TW', desc: 'Directos', url: (h) => `https://twitch.tv/${h}` },
  { id: 'kick', name: 'Kick', tag: 'KK', desc: 'Directos', url: (h) => `https://kick.com/${h}` },
  { id: 'threads', name: 'Threads', tag: 'TH', desc: 'Conversación', url: (h) => `https://www.threads.net/@${h}` },
  { id: 'facebook', name: 'Facebook', tag: 'FB', desc: 'Página · comunidad', url: (h) => `https://facebook.com/${h}` },
  { id: 'tumblr', name: 'Tumblr', tag: 'TM', desc: 'Blog · fotos', url: (h) => `https://${h}.tumblr.com` },
  { id: 'xxxfollow', name: 'XXXFollow', tag: 'XX', desc: '+18 · premium', url: (h) => `https://www.xxxfollow.com/${h}` },
  { id: 'web', name: 'Web', tag: 'WB', desc: 'Sitio oficial', url: (h) => `https://${h}` },
]

export const SOCIAL_BY_ID: Record<string, SocialPlatform> = Object.fromEntries(SOCIAL_PLATFORMS.map((p) => [p.id, p]))

/** "@valen", "valen" or a full URL → profile URL ('' when empty). */
export function socialUrl(id: string, input: string): string {
  const v = input.trim()
  if (!v) return ''
  if (/^https?:\/\//i.test(v)) return v
  const platform = SOCIAL_BY_ID[id]
  // "instagram.com/valen" without scheme
  if (v.includes('.') && v.includes('/')) return `https://${v}`
  const handle = v.replace(/^@/, '')
  return platform ? platform.url(handle) : `https://${handle}`
}

/** Short label for the right side of a link row: @handle, /handle or the host. */
export function socialHandle(id: string, url: string): string {
  try {
    const u = new URL(url)
    if (id === 'web' || u.hostname === 'example.com') {
      if (u.hostname === 'example.com') return '@' + (u.pathname.split('/').filter(Boolean).pop() || '')
      return u.hostname.replace(/^www\./, '')
    }
    if (id === 'tumblr' && u.hostname.split('.').length > 2) return '@' + u.hostname.split('.')[0]
    const first = (u.pathname.split('/').filter(Boolean)[0] || '').replace(/^@/, '')
    if (!first) return u.hostname.replace(/^www\./, '')
    return (['onlyfans', 'fansly', 'xxxfollow'].includes(id) ? '/' : '@') + first
  } catch {
    return ''
  }
}
